import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { UrlBuilder } from '../core';
import { Category } from '../documents/models/category';
import { CategoriesStorage } from '../documents';
import { SponsorStudy } from '../sponsor-dashboard/sponsor-study';
import { SponsorStudiesStorage } from '../sponsor-dashboard/sponsor-studies-storage';
import { UserPermissionsStorage } from '../user-permissions/user-permissions-storage';
import { UserPermissions } from '../user-permissions/user-permissions';
import { findCategoryId, findProtocolId } from '../shared/route-helpers';

@Injectable()
export class CanSponsorViewActivityReportsGuard implements CanActivate {

    constructor(
        private readonly urlBuilder: UrlBuilder,
        private readonly categoryStorage: CategoriesStorage,
        private readonly userPermissionsStorage: UserPermissionsStorage,
        private readonly sponsorStudiesStorage: SponsorStudiesStorage,
    ) { }

    public canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
        const protocolId = findProtocolId(route);
        const categoryId = findCategoryId(route);

        return combineLatest(
            this.userPermissionsStorage.getPermissionsByStudy(protocolId),
            this.sponsorStudiesStorage.loadStudies(),
            this.categoryStorage.activityReportCategories
        ).pipe(
            map(([permissions, studies, categories]: [UserPermissions, SponsorStudy[], Category[]]) => {
                if (studies.length === 0 || !permissions) {
                    this.urlBuilder.esa().navigate();
                    return false;
                }

                const selectedStudy = studies.find(study => study.protocolId === protocolId);
                if (!selectedStudy) {
                    this.urlBuilder.esa().navigate();
                    return false;
                }

                const canOpenCurrentCategory = permissions.canViewActivityReport(categoryId);

                if (canOpenCurrentCategory) {
                    return true;
                }

                const firstAllowedCategory = categories.find(x => permissions.canViewActivityReport(x.categoryId));

                if (firstAllowedCategory) {
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(selectedStudy)
                        .activityReports()
                        .documents(firstAllowedCategory.categoryId)
                        .navigate();
                    return false;
                }

                this.urlBuilder
                    .esa()
                    .sponsor()
                    .study(selectedStudy)
                    .activityReports()
                    .protocolAccess()
                    .navigate();
                return false;
            })
        );
    }
}